import io from 'socket.io-client';
import { eventChannel } from 'redux-saga';
import {
  take, call, put, select, all, race,
} from 'redux-saga/effects';

import { apiSendMessage, apiGetMessages, apiReadMessages } from 'services/api/chat';

import { getErrorMessage } from 'utils/filters';

import { getTriiderUser } from '../ducks/auth';
import { Creators as UiCreators, growlTypes } from '../ducks/ui';
import { Creators as ChatCreators, Types as ChatTypes, getSocket } from '../ducks/chat';
import { Creators as TaskCreators } from '../ducks/task';

const fileName = 'sagas/chat';

const events = {
  CONNECT: 'connect',
  SUBSCRIBE: 'subscribe',
  DISCONECT: 'disconnect',
  NEW_MESSAGE: 'new-message',
  MESSAGE_READ: 'message-read',
  START_TYPING: 'user-start-typing',
  STOP_TYPING: 'user-stopped-typing',
};

function connect() {
  const options = {
    // rememberUpgrade: true,
    transports: ['websocket', 'polling', 'flashsocket'],
    secure: true,
    // rejectUnauthorized: false,
  };
  const socket = io.connect(process.env.CHAT_URL, options);
  return new Promise((resolve) => {
    socket.on(events.CONNECT, () => {
      resolve(socket);
    });
    // on reconnection, reset the transports option, as the Websocket
    // connection may have failed (caused by proxy, firewall, browser, ...)
    socket.on('reconnect_attempt', () => {
      socket.io.opts.transports = ['websocket', 'polling', 'flashsocket'];
    });
  });
}

function subscribe(socket) {
  return eventChannel((emit) => {
    socket.on(events.NEW_MESSAGE, (message) => {
      const {
        user: { id: user_id, name: user_name },
        message: text,
        room,
      } = message;
      const data = {
        ...message,
        text,
        user_id,
        user_name,
        chat_id: JSON.parse(room),
        is_read: false,
      };
      emit(ChatCreators.messageReceived(data));
      emit(TaskCreators.setLastMessage(data));
    });
    socket.on(events.MESSAGE_READ, (payload) => {
      const { message: text, room } = payload;
      const chat_id = JSON.parse(room);
      emit(ChatCreators.messageRead({ messages: [{ ...payload, text, chat_id }], chat_id }));
    });
    socket.on(events.START_TYPING, (payload) => {
      emit(ChatCreators.userStartTyping(payload));
    });
    socket.on(events.STOP_TYPING, (payload) => {
      emit(ChatCreators.userStopTyping(payload));
    });
    socket.on(events.DISCONECT, (payload) => {
      emit(ChatCreators.disconectChat(payload));
    });
    return () => {
      socket.close();
    };
  });
}

function* watch(channel) {
  while (true) {
    const action = yield take(channel);
    yield put(action);
  }
}

export function* sendMessage({ payload }) {
  try {
    const { user } = yield select(getTriiderUser);

    const lastMessage = payload[payload.length - 1];
    yield put(TaskCreators.setLastMessage(lastMessage));

    const socket = yield select(getSocket, lastMessage.chat_id);

    for (let i = 0; i < payload.length; i += 1) {
      const message = {
        user,
        user_id: user.id,
        user_name: user.name,
        ...payload[i],
      };

      yield put(ChatCreators.includeMessage(message));

      if (socket) {
        socket.emit(events.NEW_MESSAGE, message);
      }
      yield call(apiSendMessage, payload[i].chat_id, message);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield all([
      put(
        UiCreators.growl(message, growlTypes.GROWL_ERROR, {
          error,
          method: `${fileName}/send`,
        }),
      ),
      put(UiCreators.hideLoader()),
    ]);
  }
}

export function* readMessages({ payload }) {
  const { messages, chat_id } = payload;
  const socket = yield select(getSocket, chat_id);
  yield put(ChatCreators.messageRead({ messages, chat_id }));
  const arrMessagesId = [];
  messages.forEach((msg) => {
    arrMessagesId.push(msg.id);
    socket.emit(events.MESSAGE_READ, { ...msg, message: msg.text, save: !!msg.id });
  });

  yield call(apiReadMessages, arrMessagesId);
}

export function* startTyping({ payload: chat_id }) {
  const triiderUser = yield select(getTriiderUser);
  const { user } = triiderUser;
  const socket = yield select(getSocket, chat_id);
  socket.emit(events.START_TYPING, {
    chat_id,
    user_name: user.name,
    user_id: user.id,
  });
}

export function* stopTyping({ payload: chat_id }) {
  const triiderUser = yield select(getTriiderUser);
  const { user } = triiderUser;
  const socket = yield select(getSocket, chat_id);
  socket.emit(events.STOP_TYPING, {
    chat_id,
    user_name: user.name,
    user_id: user.id,
  });
}

export function* initChat({ payload: chat_id }) {
  const { messages } = yield call(apiGetMessages, chat_id);
  const triiderUser = yield select(getTriiderUser);

  const socket = yield call(connect);

  socket.emit(events.SUBSCRIBE, {
    user: triiderUser.user,
    room: chat_id,
    from: 'task',
  });

  const { id: user_id } = triiderUser.user;
  const chat = {
    chat_id,
    messages,
    user_id,
    socket,
  };
  yield put(ChatCreators.initChatSuccess(chat));

  const channel = yield call(subscribe, socket);

  const { cancel } = yield race({
    task: call(watch, channel),
    cancel: take(ChatTypes.REMOVE_CHATS),
  });

  if (cancel) {
    channel.close();
  }
}
